import { defineStore } from 'pinia'
import { ref } from 'vue'

import { useLocalization } from '@/shared/composables/localization'
import { getAppURL } from '@/shared/utils/electron'
import { useFetchApi } from '@/shared/utils/fetch'
import { getCreatedEntityGuidFrom201Response } from '@/shared/utils/http'

import { BlackWhiteListEntity } from './types'

const API_URL = '/api/black-white-list'
export const useBlackWhiteListStore = defineStore('black-white-list', () => {
  const useFetch = useFetchApi()
  const { t } = useLocalization()

  const isLoading = ref(false)
  const entities = ref<Array<BlackWhiteListEntity>>([])
  const draft = ref<BlackWhiteListEntity>({
    guid: '',
    enabled: false,
    type: 'black',
  })

  const fetchAll = async (): Promise<Array<BlackWhiteListEntity>> => {
    try {
      isLoading.value = true
      const { data = [] } = await useFetch.get<BlackWhiteListEntity[]>(getAppURL({ pathName: API_URL }))
      entities.value = data
      return data
    } catch (err) {
      throw new Error(t('common.failedToLoadList', { err }))
    } finally {
      isLoading.value = false
    }
  }

  const create = async (payload: Omit<BlackWhiteListEntity, 'guid'>): Promise<BlackWhiteListEntity> => {
    try {
      isLoading.value = true
      const response = await useFetch.post<BlackWhiteListEntity>(
        getAppURL({ pathName: `${API_URL}?fillResponseBody=true` }),
        payload,
      )
      const guid = response.data?.guid ?? getCreatedEntityGuidFrom201Response(response)
      if (!guid) {
        await fetchAll()
        return { ...payload, guid: '' } // TODO: remove this if-branch when the API is fixed from 201 to 200
      } else {
        const created = { ...payload, guid }
        entities.value.push(created)
        return created
      }
    } catch (e) {
      throw new Error(t('common.failedToSaveNewElementList', { e }))
    } finally {
      isLoading.value = false
    }
  }

  const saveDraft = async (): Promise<void> => {
    await create(draft.value)
    draft.value = {
      guid: '',
      enabled: false,
      type: 'black',
    }
  }

  const remove = async (guid: BlackWhiteListEntity['guid']): Promise<boolean> => {
    try {
      isLoading.value = true
      await useFetch.delete(getAppURL({ pathName: `${API_URL}/${guid}` }))
      const index = entities.value.findIndex((entity) => entity.guid === guid)
      if (index >= 0) {
        entities.value.splice(index, 1)
      }
      return true
    } catch (e) {
      throw new Error(t('common.failedToRemoveElement', { e }))
    } finally {
      isLoading.value = false
    }
  }

  const update = async (payload: BlackWhiteListEntity): Promise<BlackWhiteListEntity> => {
    try {
      isLoading.value = true
      await useFetch.put<BlackWhiteListEntity>(getAppURL({ pathName: API_URL }), payload)
      const index = entities.value.findIndex((entity) => entity.guid === payload.guid)
      if (index >= 0) {
        entities.value[index] = payload
      }
      return payload
    } catch (e) {
      throw new Error(t('common.failedToChangeElement', { e }))
    } finally {
      isLoading.value = false
    }
  }

  return {
    create,
    draft,
    entities,
    fetchAll,
    isLoading,
    remove,
    update,
    saveDraft,
  }
})
